import * as React from 'react'
import { useGetReimbursement } from '../api/get-reimbursement'
import { isArray, isNotEmptyObject, isString } from '@clara/utils'
import { isValid, parseISO } from 'date-fns'
import { FileObject } from '@clara/file-viewer'
import { ReimbursementLabel } from '@/types/api'

interface ReimbursementOptions {
  uid?: string
}

type LoadingState = 'loading' | 'error' | 'success' | 'idle'

export function useReimbursement(options: ReimbursementOptions) {
  const { uid } = options
  const [asyncState, getReimbursement] = useGetReimbursement()

  React.useEffect(() => {
    if (typeof uid === 'undefined') return

    getReimbursement(uid)
  }, [getReimbursement, uid])

  const reimbursment = asyncState.value
  const loadingState: LoadingState = asyncState.loading
    ? 'loading'
    : asyncState.error
      ? 'error'
      : isNotEmptyObject(reimbursment)
        ? 'success'
        : 'idle'

  const isLoading = loadingState === 'loading'

  const defaultValues = React.useMemo(() => {
    const {
      amount,
      merchant,
      description,
      category,
      expenseDate: expenseDateProp,
      labels: labelsProp,
    } = reimbursment ?? {}

    const expenseDate =
      isString(expenseDateProp) && isValid(parseISO(expenseDateProp))
        ? parseISO(expenseDateProp)
        : undefined

    const labels = isArray<ReimbursementLabel>(labelsProp)
      ? labelsProp.map((label) => label.labelUUID)
      : []

    return {
      amount,
      expenseDate,
      merchant: merchant?.code,
      __merchant_name__: merchant?.name,
      category: category?.key,
      description,
      labels,
    }
  }, [reimbursment])

  const attachments = React.useMemo(() => {
    const { attachments: initialAttachments = [] } = reimbursment ?? {}
    return initialAttachments
  }, [reimbursment])

  const defaultAttachments: Array<FileObject> = React.useMemo(() => {
    return attachments.map((attachment) => ({
      uri: attachment.link,
    }))
  }, [attachments])

  return {
    loadingState,
    isLoading,
    value: reimbursment,
    defaultValues,
    defaultAttachments,
    attachments,
  }
}

export type UseReimbursementReturn = ReturnType<typeof useReimbursement>
